import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import { useAuth } from "../contexts/AuthContext";
import { Users as UsersIcon, Trash2, Shield, UserCheck, UserPlus, Building2, Link2, ChevronsUpDown, Check, X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const ROLES = [
  { value: "admin", label: "Administrator", color: "bg-purple-100 text-purple-700", needsAgency: false },
  { value: "backoffice", label: "Backoffice", color: "bg-blue-100 text-blue-700", needsAgency: false },
  { value: "manager", label: "Manager", color: "bg-amber-100 text-amber-700", needsAgency: false },
  { value: "agentie", label: "Agenție", color: "bg-teal-100 text-teal-700", needsAgency: true },
  { value: "agent", label: "Agent", color: "bg-emerald-100 text-emerald-700", needsAgency: true }
];

const Users = () => {
  const { user: currentUser, hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [createDialog, setCreateDialog] = useState(false);
  const [roleDialog, setRoleDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [newAgencyId, setNewAgencyId] = useState("");
  const [newAgentCode, setNewAgentCode] = useState("");
  const [newLinkedAgentId, setNewLinkedAgentId] = useState(null);
  const [initialLinkedAgentId, setInitialLinkedAgentId] = useState(null);
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  
  // New user form
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    name: "",
    role: "agent",
    agency_id: "",
    agent_code: ""
  });
  const [creating, setCreating] = useState(false);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Eroare la încărcarea utilizatorilor");
    } finally {
      setLoading(false);
    }
  };

  const fetchAgencies = async () => {
    try {
      const response = await axios.get(`${API_URL}/agencies`);
      setAgencies(response.data);
    } catch (error) {
      console.error("Failed to fetch agencies");
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API_URL}/agents`, { params: { limit: 500 } });
      setAgents(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch agents");
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchAgencies();
    fetchAgents();
  }, []);

  const handleCreateUser = async () => {
    // Validation
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error("Completează toate câmpurile obligatorii");
      return;
    }
    
    const selectedRole = ROLES.find(r => r.value === newUser.role);
    if (selectedRole?.needsAgency && !newUser.agency_id) {
      toast.error("Selectează o agenție pentru acest rol");
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API_URL}/users`, {
        email: newUser.email,
        password: newUser.password,
        name: newUser.name,
        role: newUser.role,
        agency_id: newUser.agency_id || null,
        agent_code: newUser.agent_code || null
      });
      toast.success("Utilizator creat cu succes!");
      setCreateDialog(false);
      setNewUser({
        email: "",
        password: "",
        name: "",
        role: "agent",
        agency_id: "",
        agent_code: ""
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la crearea utilizatorului");
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async () => {
    if (!roleDialog || !newRole) return;
    
    try {
      await axios.put(`${API_URL}/users/${roleDialog}`, {
        role: newRole,
        agency_id: newAgencyId || null,
        agent_code: newAgentCode || null
      });
      if (newLinkedAgentId !== initialLinkedAgentId) {
        const res = await axios.post(`${API_URL}/users/${roleDialog}/link-agent`, {
          agent_id: newLinkedAgentId || null
        });
        if (res.data?.synced) toast.info(res.data.synced);
      }
      toast.success("Utilizator actualizat!");
      setRoleDialog(null);
      setNewRole("");
      setNewAgencyId("");
      setNewAgentCode("");
      setNewLinkedAgentId(null);
      setInitialLinkedAgentId(null);
      fetchUsers();
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la actualizarea utilizatorului");
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    
    try {
      await axios.delete(`${API_URL}/users/${deleteDialog}`);
      toast.success("Utilizator șters");
      setDeleteDialog(null);
      fetchUsers();
    } catch (error) {
      toast.error("Eroare la ștergerea utilizatorului");
    }
  };

  const getRoleStyle = (role) => {
    return ROLES.find(r => r.value === role)?.color || "bg-slate-100 text-slate-700";
  };

  const getRoleName = (role) => {
    return ROLES.find(r => r.value === role)?.label || role;
  };
  
  const selectedRoleNeedsAgency = ROLES.find(r => r.value === newUser.role)?.needsAgency || false;
  const editRoleNeedsAgency = ROLES.find(r => r.value === newRole)?.needsAgency || false;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Utilizatori</h1>
          <p className="text-slate-500 mt-1">
            Gestionează utilizatorii și rolurile acestora
          </p>
        </div>
        {hasPermission && hasPermission("users", "create") && (
          <Button 
            className="bg-slate-900 hover:bg-slate-800"
            onClick={() => setCreateDialog(true)}
            data-testid="create-user-btn"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Utilizator Nou
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {ROLES.map((role) => {
          const count = users.filter(u => u.role === role.value).length;
          return (
            <Card key={role.value}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{role.label}</p>
                    <p className="text-2xl font-bold text-slate-900">{count}</p>
                  </div>
                  <div className={`p-3 rounded-full ${role.color}`}>
                    <Shield className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UsersIcon className="h-5 w-5" />
            Listă Utilizatori
          </CardTitle>
          <CardDescription>
            {users.length} utilizatori înregistrați
          </CardDescription>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <UsersIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nu există utilizatori</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nume</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Agenție</TableHead>
                  <TableHead>Cod Agent</TableHead>
                  <TableHead>Agent CRM Vechi</TableHead>
                  <TableHead>Data Înregistrării</TableHead>
                  <TableHead className="text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge className={getRoleStyle(user.role)}>
                        {getRoleName(user.role)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.agency_name ? (
                        <div className="flex items-center gap-1">
                          <Building2 className="h-3 w-3 text-slate-400" />
                          {user.agency_name}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.agent_code ? (
                        <span className="font-mono text-sm bg-slate-100 px-2 py-0.5 rounded" data-testid={`agent-code-${user.id}`}>{user.agent_code}</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.linked_agent_name ? (
                        <div className="flex items-center gap-1" data-testid={`linked-agent-${user.id}`}>
                          <Link2 className="h-3 w-3 text-emerald-500 shrink-0" />
                          <span className="text-sm">{user.linked_agent_name}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {new Date(user.created_at).toLocaleDateString('ro-RO')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setRoleDialog(user.id);
                            setNewRole(user.role);
                            setNewAgencyId(user.agency_id || "");
                            setNewAgentCode(user.agent_code || "");
                            setNewLinkedAgentId(user.linked_agent_id || null);
                            setInitialLinkedAgentId(user.linked_agent_id || null);
                          }}
                          disabled={user.id === currentUser?.id}
                          data-testid={`edit-role-${user.id}`}
                        >
                          <UserCheck className="h-4 w-4 mr-1" />
                          Editează
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteDialog(user.id)}
                          disabled={user.id === currentUser?.id}
                          data-testid={`delete-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Crează Utilizator Nou</DialogTitle>
            <DialogDescription>
              Completează datele pentru noul utilizator
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nume *</Label>
              <Input
                placeholder="Ex: Ion Popescu"
                value={newUser.name}
                onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                data-testid="new-user-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="email@exemplu.ro"
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                data-testid="new-user-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Parolă *</Label>
              <Input
                type="password"
                placeholder="Minim 6 caractere"
                value={newUser.password}
                onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                data-testid="new-user-password"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Rol *</Label>
              <Select 
                value={newUser.role} 
                onValueChange={(value) => setNewUser(prev => ({ ...prev, role: value, agency_id: "" }))}
              >
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue placeholder="Selectează rol" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedRoleNeedsAgency && (
              <div className="space-y-2">
                <Label>Agenție *</Label>
                <Select 
                  value={newUser.agency_id} 
                  onValueChange={(value) => setNewUser(prev => ({ ...prev, agency_id: value }))}
                >
                  <SelectTrigger data-testid="new-user-agency">
                    <SelectValue placeholder="Selectează agenție" />
                  </SelectTrigger>
                  <SelectContent>
                    {agencies.map((agency) => (
                      <SelectItem key={agency.id} value={agency.id}>
                        {agency.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Rolurile Agent și Agenție necesită o agenție asociată
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Cod Agent (Marca)</Label>
              <Input
                placeholder="Ex: 12345"
                value={newUser.agent_code}
                onChange={(e) => setNewUser(prev => ({ ...prev, agent_code: e.target.value }))}
                data-testid="new-user-agent-code"
              />
              <p className="text-xs text-slate-500">
                Folosit la importul Excel pentru a asocia vânzările cu agentul corect
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>
              Anulează
            </Button>
            <Button onClick={handleCreateUser} disabled={creating} data-testid="confirm-create-user">
              {creating ? "Se creează..." : "Creează"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={!!roleDialog} onOpenChange={() => setRoleDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editează Utilizator</DialogTitle>
            <DialogDescription>
              Modifică rolul și agenția utilizatorului
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select value={newRole} onValueChange={(value) => {
                setNewRole(value);
                // Clear agency if role doesn't need it
                const roleNeedsAgency = ROLES.find(r => r.value === value)?.needsAgency;
                if (!roleNeedsAgency) {
                  setNewAgencyId("");
                }
              }}>
                <SelectTrigger data-testid="role-select">
                  <SelectValue placeholder="Selectează rol" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {editRoleNeedsAgency && (
              <div className="space-y-2">
                <Label>Agenție</Label>
                <Select value={newAgencyId} onValueChange={setNewAgencyId}>
                  <SelectTrigger data-testid="agency-select">
                    <SelectValue placeholder="Selectează agenție" />
                  </SelectTrigger>
                  <SelectContent>
                    {agencies.map((agency) => (
                      <SelectItem key={agency.id} value={agency.id}>
                        {agency.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Cod Agent (Marca)</Label>
              <Input
                placeholder="Ex: 12345"
                value={newAgentCode}
                onChange={(e) => setNewAgentCode(e.target.value)}
                data-testid="edit-agent-code"
              />
              <p className="text-xs text-slate-500">
                Folosit la importul Excel pentru a asocia vânzările cu agentul corect
              </p>
            </div>

            <div className="space-y-2">
              <Label>Agent CRM Vechi (alias)</Label>
              <div className="flex gap-2">
                <Popover open={agentPickerOpen} onOpenChange={setAgentPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="flex-1 justify-between font-normal"
                      data-testid="edit-linked-agent-trigger"
                    >
                      {newLinkedAgentId
                        ? (() => {
                            const a = agents.find(x => x.id === newLinkedAgentId);
                            return a ? `${a.full_name}${a.primary_marca_code ? ` [${a.primary_marca_code}]` : ""}` : "Agent selectat";
                          })()
                        : "Selectează agentul din CRM vechi..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[420px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Caută după nume sau marcă..." data-testid="agent-search-input" />
                      <CommandList className="max-h-64">
                        <CommandEmpty>Niciun agent găsit</CommandEmpty>
                        <CommandGroup>
                          {agents.map((a) => {
                            const takenByOther = a.linked_user_id && a.linked_user_id !== roleDialog;
                            return (
                              <CommandItem
                                key={a.id}
                                value={`${a.full_name || ""} ${a.primary_marca_code || ""}`}
                                disabled={takenByOther}
                                onSelect={() => {
                                  setNewLinkedAgentId(a.id);
                                  if (a.primary_marca_code && !newAgentCode) {
                                    setNewAgentCode(a.primary_marca_code);
                                  }
                                  setAgentPickerOpen(false);
                                }}
                                data-testid={`agent-option-${a.id}`}
                              >
                                <Check className={`mr-2 h-4 w-4 ${newLinkedAgentId === a.id ? "opacity-100" : "opacity-0"}`} />
                                <div className="flex-1 min-w-0">
                                  <span className="truncate">{a.full_name}</span>
                                  {a.primary_marca_code && (
                                    <span className="ml-2 font-mono text-xs text-slate-500">[{a.primary_marca_code}]</span>
                                  )}
                                  {a.agency_name && (
                                    <span className="ml-2 text-xs text-slate-400">{a.agency_name}</span>
                                  )}
                                </div>
                                {takenByOther && (
                                  <Badge variant="outline" className="ml-2 text-xs shrink-0">asociat</Badge>
                                )}
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {newLinkedAgentId && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setNewLinkedAgentId(null)}
                    data-testid="unlink-agent-btn"
                    title="Dezasociază agentul"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-slate-500">
                Leagă contul de identitatea agentului din CRM vechi (aliasuri import). Codul de agent se preia automat dacă lipsește.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialog(null)}>
              Anulează
            </Button>
            <Button onClick={handleRoleChange} disabled={!newRole} data-testid="confirm-role">
              Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmare Ștergere</DialogTitle>
            <DialogDescription>
              Ești sigur că vrei să ștergi acest utilizator? Această acțiune nu poate fi anulată.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>
              Anulează
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete">
              Șterge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
